import { type ReferralType } from '../../../../../model/referral'
import {
  isValidEmail,
  isValidPhone,
  normalizeEmail,
  phoneMatchKey
} from '../../../../../utils/contactUtils'

export interface ReferralFormValues {
  refereeName?: string
  refereeEmail?: string
  refereePhone?: string
}

/** Keys of `messages`, so this stays i18n-free and unit-testable. */
type ReferralErrorKey =
  | 'nameRequired'
  | 'contactRequired'
  | 'invalidEmail'
  | 'invalidPhone'
  | 'selfReferral'
  | 'duplicateReferral'

type ReferralFormErrors = Partial<
  Record<keyof ReferralFormValues, ReferralErrorKey>
>

interface Context {
  /** The referring doctor's own email, to catch self-referral. */
  ownEmail: string
  /** The referring doctor's own phone, to catch self-referral. */
  ownPhone: string
  /** Referrals already made by this doctor, to catch duplicates. */
  existing: ReferralType[]
}

/**
 * Validates the referral form.
 *
 * The self-referral and duplicate checks are conveniences to keep honest users
 * out of trouble — they are not security boundaries. Every write here comes
 * straight from the browser, so the team's manual verification step is the
 * real gate. In particular, the same colleague referred by *two different*
 * doctors is not detectable here; the team catches that at approval time via
 * the `refereeEmailKey` / `refereePhoneKey` fields.
 */
export function validateReferralForm(
  values: ReferralFormValues,
  { ownEmail, ownPhone, existing }: Context
): ReferralFormErrors {
  const errors: ReferralFormErrors = {}

  const name = values.refereeName?.trim() ?? ''
  const rawEmail = values.refereeEmail?.trim() ?? ''
  const rawPhone = values.refereePhone?.trim() ?? ''

  if (!name) {
    errors.refereeName = 'nameRequired'
  }

  // At least one way to reach the referred colleague is required.
  if (!rawEmail && !rawPhone) {
    errors.refereeEmail = 'contactRequired'
    errors.refereePhone = 'contactRequired'
    return errors
  }

  if (rawEmail && !isValidEmail(rawEmail)) {
    errors.refereeEmail = 'invalidEmail'
  }
  if (rawPhone && !isValidPhone(rawPhone)) {
    errors.refereePhone = 'invalidPhone'
  }

  const email = normalizeEmail(rawEmail)
  const phone = phoneMatchKey(rawPhone)

  if (!errors.refereeEmail && email && email === normalizeEmail(ownEmail)) {
    errors.refereeEmail = 'selfReferral'
  }
  if (!errors.refereePhone && phone && phone === phoneMatchKey(ownPhone)) {
    errors.refereePhone = 'selfReferral'
  }

  if (
    !errors.refereeEmail &&
    email &&
    existing.some((r) => normalizeEmail(r.refereeEmail) === email)
  ) {
    errors.refereeEmail = 'duplicateReferral'
  }
  if (
    !errors.refereePhone &&
    phone &&
    existing.some((r) => phoneMatchKey(r.refereePhone) === phone)
  ) {
    errors.refereePhone = 'duplicateReferral'
  }

  return errors
}
